import { Box, Tooltip, Typography } from "@mui/material";
import { forwardRef, useState } from "react";
import { NavLink as RouterLink } from "react-router-dom";
import {
  ListItemIconStyle,
  ListItemStyle,
  ListItemStyleProps,
  ListItemTextStyle,
} from "./style";
import { NavItemProps } from "./type";

//theme
import { useTheme } from "@mui/material/styles";
import useLocales from "hooks/useLocales";

// import ChevronUpIcon from "src/assets/icon/chevron/up";
// import PermissionInfo from "src/components/permissions/permissionInfo";
import { isExternalLink } from "utils/routes";
import NavItemPopover from "./NavItemPopover";
// import NavItemSubmenuPopover from "./NavItemSubmenuPopover";
import ChevronDown from "assets/icon/chevron/ChevronDown";
import ChevronUpIcon from "assets/icon/chevron/up";
import PermissionInfo from "components/permissionInfo";
import NavItemSubmenuPopover from "./NavItemSubMenuPopover";

// ----------------------------------------------------------------------

// HANDLE SHOW ITEM BY ROLE
export const ListItem = forwardRef<
  HTMLDivElement & HTMLAnchorElement,
  ListItemStyleProps
>((props, ref) => (
  <ListItemStyle {...props} ref={ref}>
    {props.children}
  </ListItemStyle>
));

export function NavItemRoot({
  item,
  isCollapse,
  open = false,
  active,
  onOpen,
}: NavItemProps) {
  const { translate } = useLocales();
  const {
    title,
    path,
    icon,
    info,
    locked,
    children,
    disabled,
    caption,
    roles,
  } = item;
  const theme = useTheme();
  const textColor = theme.palette?.text?.greyLight;
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [submenunchorEl, setSubmenuAnchorEl] = useState<HTMLElement | null>(
    null
  );
  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    handleCloseSubmenuPopover();
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };
  const handleOpenSubmenuPopover = (event: React.MouseEvent<HTMLElement>) => {
    if (locked) return;
    setSubmenuAnchorEl(event.currentTarget);
  };
  const handleCloseSubmenuPopover = () => {
    setSubmenuAnchorEl(null);
  };
  const renderContent = (
    <>
      {isCollapse && icon && !submenunchorEl && (
        <NavItemPopover {...{ anchorEl, handlePopoverClose, title, locked }} />
      )}
      {isCollapse && icon && (
        <NavItemSubmenuPopover
          {...{
            anchorEl: submenunchorEl,
            handlePopoverClose: handlePopoverClose,
            submenu: children,
            title,
            locked,
          }}
        />
      )}
      {icon && (
        <ListItemIconStyle
          sx={{
            ...(isCollapse
              ? {
                  mr: "0px",
                }
              : {}),
          }}
        >
          {icon}
        </ListItemIconStyle>
      )}
      <ListItemTextStyle
        sx={{ textTransform: "initial", color: textColor, fontWeight: 700 }}
        disableTypography
        primary={translate(title)}
        secondary={
          <Tooltip title={translate(caption) || ""} arrow>
            <Typography
              noWrap
              variant="caption"
              component="div"
              sx={{ textTransform: "initial" }}
            >
              {translate(caption)}
            </Typography>
          </Tooltip>
        }
        isCollapse={isCollapse}
      />
      {!isCollapse && (
        <Box
          sx={{
            gap: 0.5,
            display: "flex",
          }}
        >
          {info && info}
          {locked ? <PermissionInfo /> : children && <ArrowIcon open={open} />}
        </Box>
      )}
    </>
  );

  if (children) {
    return (
      <ListItem
        onClick={(e) => {
          if (!locked) {
            onOpen?.();
          }
          if (!submenunchorEl) {
            handleOpenSubmenuPopover(e);
            handlePopoverClose();
          } else handleCloseSubmenuPopover();
        }}
        onMouseEnter={handlePopoverOpen}
        onMouseLeave={handlePopoverClose}
        activeRoot={active}
        disabled={disabled}
        roles={roles}
        sx={{
          "&:hover": {
            bgcolor: "#ffffff",
          },
          "&.activeRoot": {
            bgcolor: "#ffffff",
          },
          "&.active": {
            bgcolor: "#ffffff",
          },
        }}
      >
        {renderContent}
      </ListItem>
    );
  }

  return isExternalLink(path) ? (
    <ListItem
      {...(!locked
        ? {
            component: RouterLink,
            to: path,
          }
        : {})}
      onMouseEnter={handlePopoverOpen}
      onMouseLeave={handlePopoverClose}
      target="_blank"
      rel="noopener"
      disabled={disabled}
      roles={roles}
      sx={{
        "&:hover": {
          bgcolor: "#ffffff",
        },
        "&.activeRoot": {
          bgcolor: "#ffffff",
        },
        "&.active": {
          bgcolor: "#ffffff",
        },
      }}
    >
      {renderContent}
    </ListItem>
  ) : (
    <ListItem
      {...(!locked
        ? {
            component: RouterLink,
            to: path,
          }
        : {})}
      onMouseEnter={handlePopoverOpen}
      onMouseLeave={handlePopoverClose}
      activeRoot={active}
      disabled={disabled}
      roles={roles}
      sx={{
        "&:hover": {
          bgcolor: "#ffffff",
        },
        "&.activeRoot": {
          bgcolor: "#ffffff",
        },
        "&.active": {
          bgcolor: "#ffffff",
        },
      }}
    >
      {renderContent}
    </ListItem>
  );
}

// ----------------------------------------------------------------------

type NavItemSubProps = Omit<NavItemProps, "isCollapse">;

export function NavItemSub({
  item,
  open = false,
  active = false,
  onOpen,
}: NavItemSubProps) {
  const { translate } = useLocales();

  const {
    title,
    path,
    info,
    children,
    locked,
    color,
    disabled,
    caption,
    roles,
  } = item;

  const renderContent = (
    <>
      <DotIcon active={active} color={color} />
      <ListItemTextStyle
        primary={translate(title)}
        disableTypography
        sx={{
          color: "text.greyLight",
          fontWeight: active ? "bold" : "normal",
          fontSize: "12px",
        }}
        secondary={
          <Tooltip title={translate(caption) || ""} arrow>
            <Typography
              noWrap
              variant="caption"
              component="div"
              sx={{ textTransform: "initial", color: "text.greyLight" }}
            >
              {translate(caption)}
            </Typography>
          </Tooltip>
        }
      />
      {info && info}
      {locked ? <PermissionInfo /> : children && <ArrowIcon open={open} />}
    </>
  );

  if (children) {
    return (
      <ListItem
        onClick={locked ? undefined : onOpen}
        activeSub={active}
        subItem
        disabled={disabled}
        roles={roles}
        sx={{
          "&:hover": {
            bgcolor: "#ffffff",
          },
          "&.activeRoot": {
            bgcolor: "#ffffff",
          },
          "&.active": {
            bgcolor: "#ffffff",
          },
        }}
      >
        {renderContent}
      </ListItem>
    );
  }

  return isExternalLink(path) ? (
    <ListItem
      {...(!locked
        ? {
            component: RouterLink,
            to: path,
          }
        : {})}
      target="_blank"
      rel="noopener"
      subItem
      disabled={disabled}
      roles={roles}
      sx={{
        "&:hover": {
          bgcolor: "#ffffff",
        },
        "&.activeRoot": {
          bgcolor: "#ffffff",
        },
        "&.active": {
          bgcolor: "#ffffff",
        },
      }}
    >
      {renderContent}
    </ListItem>
  ) : (
    <ListItem
      {...(!locked
        ? {
            component: RouterLink,
            to: path,
          }
        : {})}
      activeSub={active}
      subItem
      disabled={disabled}
      roles={roles}
      sx={{
        ml: 2,
        "&:hover": {
          bgcolor: "#ffffff",
        },
        "&.activeRoot": {
          bgcolor: "#ffffff",
        },
        "&.active": {
          bgcolor: "#ffffff",
        },
      }}
    >
      {renderContent}
    </ListItem>
  );
}

export function DotIcon({
  active,
  color,
}: {
  active: boolean;
  color: string | undefined;
}) {
  return (
    <ListItemIconStyle
      sx={{
        p: 0,
      }}
    >
      <Box
        component="span"
        sx={{
          width: "5px",
          height: "5px",
          borderRadius: "50%",
          bgcolor: active ? color : "text.disabled",
          transition: (theme) =>
            theme.transitions.create("transform", {
              duration: theme.transitions.duration.shorter,
            }),
        }}
      />
    </ListItemIconStyle>
  );
}

// ----------------------------------------------------------------------

type ArrowIconProps = {
  open: boolean;
};

export function ArrowIcon({ open }: ArrowIconProps) {
  return <>{open ? <ChevronUpIcon /> : <ChevronDown />}</>;
}
